import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'student' | 'admin' | 'admin_smp' | 'admin_smk' | 'admin_ponpes';
  isProfileCompleted?: boolean;
  avatar_url?: string;
  isProfileLocked?: boolean;
  is_active?: boolean;
}

interface AppState {
  isMenuOpen: boolean;
  toggleMenu: () => void;
  closeMenu: () => void;
  selectedTenant: string | null;
  setSelectedTenant: (tenant: string | null) => void;

  // Auth State
  user: User | null;
  isAuthenticated: boolean;
  isHydrated: boolean;
  login: (user: User) => void;
  logout: () => void;
}

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      isMenuOpen: false,
      toggleMenu: () => set((state) => ({ isMenuOpen: !state.isMenuOpen })),
      closeMenu: () => set({ isMenuOpen: false }),
      selectedTenant: null,
      setSelectedTenant: (tenant) => set({ selectedTenant: tenant }),

      // Auth
      user: null,
      isAuthenticated: false,
      isHydrated: true, // Set default true untuk instant UI, akan di-update oleh rehydration
      login: (user) => set({ user, isAuthenticated: true }),
      logout: () => {
        // Clear Zustand persisted state
        set({ user: null, isAuthenticated: false });

        // Clear all localStorage items related to user session
        // Use try-catch untuk handle Cloudflare/Quota issues
        try {
          localStorage.removeItem('app-storage');
          localStorage.removeItem('hasSeenDashboardTour');
          localStorage.removeItem('profileTourShown');
          localStorage.removeItem('hasSeenAdminTour');

          // Clear Supabase auth tokens (keys starting with 'sb-')
          const keysToRemove = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('sb-')) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach((key) => localStorage.removeItem(key));
        } catch (e) {
          // Silent fail - Cloudflare bisa block localStorage operations
          console.warn('Failed to clear localStorage:', e);
        }

        // Clear sessionStorage if any
        try {
          sessionStorage.clear();
        } catch (e) {
          // Silent fail
        }
      },
    }),
    {
      name: 'app-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        selectedTenant: state.selectedTenant,
        // Note: isHydrated tidak di-persist, selalu true untuk instant UI
      }),
      // Optimize rehydration - mark as hydrated immediately untuk UI cepat
      // Critical untuk mencegah blocking UI saat Cloudflare menambah latency
      skipHydration: false,
      // Fast rehydration - set hydrated immediately untuk instant UI
      onRehydrateStorage: () => {
        // Return callback yang akan dipanggil setelah rehydration
        return (state, error) => {
          if (error) {
            console.error('Error rehydrating store:', error);
          }

          // CRITICAL: Set hydrated to true immediately setelah rehydration
          // Ini memastikan UI tidak terblokir menunggu localStorage
          // State sudah di-merge dari localStorage oleh Zustand
          if (state) {
            state.isHydrated = true;
          }
        };
      },
    },
  ),
);
